import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import type { MailSourceType } from '@symphenia/shared-types';
import { Button } from '@/components/ui/button';
import { dropdownMenu, dropdownItem } from '@/lib/form-styles';
import { downloadFile, extractFilename, EXPORT_MIME_TYPES, type ExportFormat } from '@/lib/download';
import { exportLaden, type ExportRequest } from '@/lib/export-request';
import { MailExportDialog } from '@/components/mail/MailExportDialog';

export interface ExportOption {
  format: ExportFormat;
  /** Feste Beschriftung … */
  label?: string;
  /** … oder ein i18n-Schlüssel; er gewinnt, wenn beides gesetzt ist. */
  labelKey?: string;
}

/**
 * AP-06-B Regel 3 — Ausrichtung beim Export.
 *
 * 'default' schickt nichts mit: Dann entscheidet der Mandanten-Standard aus dem
 * Branding (und in dessen Automatik-Modus die Spaltenzahl). Erst eine
 * ausdrückliche Wahl überschreibt ihn, und nur für diesen einen Export.
 */
export type ExportOrientationChoice = 'default' | 'portrait' | 'landscape';

const ORIENTATION_CHOICES: { value: ExportOrientationChoice; labelKey: string }[] = [
  { value: 'default',   labelKey: 'export.orientationDefault' },
  { value: 'portrait',  labelKey: 'export.orientationPortrait' },
  { value: 'landscape', labelKey: 'export.orientationLandscape' },
];

const DEFAULT_OPTIONS: ExportOption[] = [
  { format: 'xlsx', labelKey: 'export.xlsx' },
  { format: 'docx', labelKey: 'export.docx' },
  { format: 'pdf',  labelKey: 'export.pdf'  },
];

/**
 * AP-10 Nachtrag 5 — **Pflicht: Jeder Export lässt sich auch per E-Mail senden**
 * (Auftraggeber, 16.9.2026). Die Prop ist bewusst nicht optional: Ein neuer
 * Export-Knopf ohne sie kompiliert nicht. Gegenstelle im Backend ist
 * `POST <url>/mail`, erzwungen von `export-per-mail.spec.ts`.
 */
export interface ExportMailConfig {
  /** Schlüssel fürs Versandprotokoll — neue Exporte ergänzen `MAIL_SOURCE_TYPES`. */
  source: MailSourceType;
  /** Name des Exports im Anhang, z. B. „Tagesarbeitszeiten". */
  title: string;
  /** Vorgabe-Betreff, frei änderbar. */
  subject: string;
}

interface ExportButtonProps {
  /**
   * Welcher Export mit welchen Parametern — für das gewählte Format und die
   * gewählte Ausrichtung. Ohne Ausrichtung gilt der Mandanten-Standard. Daraus
   * entstehen der Download und, mit denselben Parametern, der Mailversand.
   */
  request: (format: ExportFormat, orientation?: 'portrait' | 'landscape') => ExportRequest;
  /** Mailversand — Pflicht, siehe `ExportMailConfig`. */
  mail: ExportMailConfig;
  /** Auswahl der Seitenausrichtung anbieten (Default: ja) */
  showOrientation?: boolean;
  /** Standard-Dateiname wenn Content-Disposition fehlt */
  defaultFilename?: string;
  /** Welche Formate angeboten werden (Default: xlsx, docx, pdf) */
  options?: ExportOption[];
  /** Zusätzliche CSS-Klassen für den Button */
  className?: string;
  /** Button-Label (Default: übersetztes „Exportieren") */
  label?: string;
  /** Zusätzliche Wahl oben im Menü (z. B. ein Kontrollkästchen, das in `request` einfließt) */
  extra?: ReactNode;
}

/**
 * ExportButton – Dropdown-Button für XLSX / DOCX / PDF Export.
 *
 * Verwendung:
 * ```tsx
 * <ExportButton
 *   defaultFilename="zeiterfassung"
 *   request={(format, orientation) => timeEntriesApi.exportRequest({ ...filterParams, format, orientation })}
 *   mail={{ source: 'EXPORT_TIME_ENTRIES', title: 'Zeiterfassung', subject: 'Zeiterfassung 09/2026' }}
 * />
 * ```
 */
export function ExportButton({
  request,
  mail,
  showOrientation = true,
  defaultFilename = 'export',
  options = DEFAULT_OPTIONS,
  className,
  label,
  extra,
}: ExportButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [orientation, setOrientation] = useState<ExportOrientationChoice>('default');
  const [mailOffen, setMailOffen] = useState(false);

  const handleExport = async (format: ExportFormat) => {
    setLoading(format);
    setOpen(false);
    setError(null);
    try {
      const response = await exportLaden(request(format, orientation === 'default' ? undefined : orientation));

      // Fehler-Antwort als ArrayBuffer dekodieren (z.B. HTTP 4xx/5xx mit JSON-Body)
      if (response.status >= 400) {
        const text = new TextDecoder().decode(response.data);
        let msg = t('export.serverError', { status: response.status });
        try { msg = JSON.parse(text)?.message ?? msg; } catch { /* ignore */ }
        setError(msg);
        return;
      }

      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      const filename = extractFilename(contentDisposition, `${defaultFilename}.${format}`);
      const mimeType = (response.headers['content-type'] as string | undefined)?.split(';')[0] ?? EXPORT_MIME_TYPES[format];
      downloadFile(response.data, filename, mimeType);
    } catch (err: unknown) {
      console.error('Export fehlgeschlagen:', err);

      // Axios-Fehler: Fehlermeldung aus dem Response-Body lesen
      let msg = t('export.failed');
      const axiosErr = err as {
        response?: { data?: ArrayBuffer | string; status?: number };
        code?: string;
      };
      // Kam gar keine Antwort, ist der Server nicht erreichbar (oder startet
      // gerade neu). Das ist etwas anderes als ein fehlgeschlagener Export und
      // muss auch anders dastehen — sonst sucht man den Fehler im Export.
      if (!axiosErr?.response) {
        msg =
          axiosErr?.code === 'ECONNABORTED'
            ? t('export.timeout')
            : t('export.serverUnreachable');
      } else if (axiosErr?.response) {
        const status = axiosErr.response.status ?? '';
        const raw = axiosErr.response.data;
        if (raw instanceof ArrayBuffer) {
          try {
            const parsed = JSON.parse(new TextDecoder().decode(raw));
            msg = `${status}: ${parsed?.message ?? parsed?.error ?? JSON.stringify(parsed)}`;
          } catch {
            msg = t('export.serverError', { status });
          }
        } else if (typeof raw === 'string') {
          msg = `${status}: ${raw}`;
        } else {
          msg = t('export.serverError', { status });
        }
      }
      setError(msg);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className={`relative inline-block ${className ?? ''}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => { setOpen((v) => !v); setError(null); }}
        disabled={loading !== null}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {loading ? (
          <>
            <span className="mr-2 h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            {t('export.running', { format: loading.toUpperCase() })}
          </>
        ) : (
          <>
            <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            {label ?? t('export.button')}
            <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </Button>

      {open && (
        <>
          {/* Backdrop zum Schließen */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <ul
            role="listbox"
            className={`${dropdownMenu} right-0 mt-1`}
            style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
          >
            {extra && <li className="border-b border-border px-3 py-2 text-sm">{extra}</li>}
            {showOrientation && (
              <li className="border-b border-border px-3 py-2">
                <div className="mb-1.5 text-xs text-muted-foreground">{t('export.orientation')}</div>
                <div className="flex gap-1">
                  {ORIENTATION_CHOICES.map((choice) => (
                    <button
                      key={choice.value}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setOrientation(choice.value); }}
                      aria-pressed={orientation === choice.value}
                      className={`flex-1 rounded-md border px-2 py-1 text-xs transition-colors ${
                        orientation === choice.value
                          ? 'border-primary bg-primary/10 text-primary font-medium'
                          : 'border-input text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t(choice.labelKey)}
                    </button>
                  ))}
                </div>
              </li>
            )}
            {options.map((opt) => (
              <li key={opt.format}>
                <button
                  className={dropdownItem}
                  style={{ color: 'hsl(var(--card-foreground))' }}
                  onClick={() => handleExport(opt.format)}
                >
                  {opt.labelKey ? t(opt.labelKey) : opt.label}
                </button>
              </li>
            ))}
            {/* AP-10 Nachtrag 5: derselbe Export per E-Mail */}
            <li className="border-t border-border">
              <button
                className={`${dropdownItem} flex items-center gap-2`}
                style={{ color: 'hsl(var(--card-foreground))' }}
                onClick={() => { setOpen(false); setMailOffen(true); }}
              >
                <Mail className="h-4 w-4" />
                {t('mailExport.menu')}
              </button>
            </li>
          </ul>
        </>
      )}

      <MailExportDialog
        open={mailOffen}
        onClose={() => setMailOffen(false)}
        source={mail.source}
        title={mail.title}
        defaultSubject={mail.subject}
        request={request}
        formats={options.map((o) => o.format)}
        showOrientation={showOrientation}
        initialOrientation={orientation}
      />

      {/* Fehlermeldung unterhalb des Buttons */}
      {error && (
        <div className="absolute right-0 top-full z-30 mt-1 max-w-xs rounded-md border border-destructive bg-popover px-3 py-2 text-xs text-destructive shadow-md">
          <div className="flex items-start gap-1.5">
            <svg className="mt-0.5 h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <span>{error}</span>
          </div>
          <button
            className="absolute right-2 top-1.5 text-muted-foreground hover:text-foreground"
            onClick={() => setError(null)}
          >✕</button>
        </div>
      )}
    </div>
  );
}
